/**
 * Few-shot examples editor — list of NL question + SQL pairs.
 *
 * Shape of each `example` row in `report_training`:
 *   - `name` → short label shown in the list
 *   - `content` → the SQL answer
 *   - `metadata.question` → the natural-language prompt
 *   - `metadata.explanation` (optional) → rationale shown to the LLM
 */

import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { useT } from "@/i18n";
import { useTrainingRows } from "./useTrainingRows";
import type { TrainingItem } from "@/lib/training-api";

interface ExamplesEditorProps {
  draftKey: string;
}

interface ExampleMetadata {
  question?: string;
  explanation?: string;
  [key: string]: unknown;
}

function readMeta(row: TrainingItem): ExampleMetadata {
  return (row.metadata ?? {}) as ExampleMetadata;
}

export function ExamplesEditor({ draftKey }: ExamplesEditorProps): JSX.Element {
  const { t } = useT();
  const { rows, loading, error, create, update, remove } = useTrainingRows(
    draftKey,
    "example",
  );
  const [createError, setCreateError] = useState<string | null>(null);

  if (loading) {
    return <p className="text-sm text-neutral-500">{t("editor.ai.loading")}</p>;
  }
  if (error) {
    return <p className="text-sm text-error-dark">{error}</p>;
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-neutral-200 px-3 py-4 text-center text-xs text-neutral-500">
          {t("editor.ai.noExamplesHint")}
        </p>
      ) : (
        rows.map((row, idx) => (
          <ExampleRow
            key={row.id}
            row={row}
            index={idx}
            onSave={(payload) => update(row.id, payload)}
            onDelete={() => remove(row.id)}
          />
        ))
      )}
      <button
        type="button"
        onClick={() =>
          void create({
            name: `example-${rows.length + 1}`,
            content: "",
            metadata: { question: "", explanation: "" },
            position: rows.length,
          }).then(
            () => setCreateError(null),
            (err: unknown) =>
              setCreateError(err instanceof Error ? err.message : String(err)),
          )
        }
        className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
      >
        <Plus className="h-3.5 w-3.5" /> {t("editor.ai.addExampleBtn")}
      </button>
      {createError && (
        <p className="text-xs text-error-dark">{createError}</p>
      )}
    </div>
  );
}

interface ExampleRowProps {
  row: TrainingItem;
  index: number;
  onSave: (payload: {
    name: string;
    content: string;
    metadata: ExampleMetadata;
  }) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}

function ExampleRow({
  row,
  index,
  onSave,
  onDelete,
}: ExampleRowProps): JSX.Element {
  const { t } = useT();
  const initialMeta = readMeta(row);
  const [name, setName] = useState(row.name);
  const [question, setQuestion] = useState(initialMeta.question ?? "");
  const [explanation, setExplanation] = useState(initialMeta.explanation ?? "");
  const [content, setContent] = useState(row.content);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setName(row.name);
    setQuestion(initialMeta.question ?? "");
    setExplanation(initialMeta.explanation ?? "");
    setContent(row.content);
  }, [row.id, row.name, row.content, initialMeta.question, initialMeta.explanation]);

  const dirty =
    name !== row.name ||
    content !== row.content ||
    question !== (initialMeta.question ?? "") ||
    explanation !== (initialMeta.explanation ?? "");

  async function handleSave(): Promise<void> {
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        name,
        content,
        metadata: { ...initialMeta, question, explanation },
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs text-neutral-500">
          {t("editor.ai.exampleLabel", { idx: index + 1 })}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
            className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
          >
            {saving ? t("editor.ai.saving") : t("editor.ai.saveBtn")}
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm(t("editor.ai.deleteConfirm"))) {
                onDelete().catch((err: unknown) =>
                  setSaveError(err instanceof Error ? err.message : String(err)),
                );
              }
            }}
            className="grid h-7 w-7 place-items-center rounded text-neutral-500 hover:bg-error/10 hover:text-error-dark"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="mb-2">
        <label className="block text-xs text-neutral-500">
          {t("editor.ai.exampleNameLabel")}
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary font-mono text-xs"
        />
      </div>

      <div className="mb-2">
        <label className="block text-xs text-neutral-500">
          {t("editor.ai.questionLabel")}
        </label>
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t("editor.ai.questionPlaceholder")}
          className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="mb-2">
        <label className="block text-xs text-neutral-500">
          {t("editor.ai.exampleSqlLabel")}
        </label>
        {/* SQL bodies for training examples are non-trivial. Show
           ~12 rows by default with ``resize-y`` so the admin can grow
           further if the example uses multiple CTEs. */}
        <textarea
          rows={12}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onBlur={() => void handleSave()}
          placeholder={t("editor.ai.exampleSqlPlaceholder")}
          className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary min-h-[260px] resize-y font-mono text-xs leading-relaxed"
        />
      </div>

      <div>
        <label className="block text-xs text-neutral-500">
          {t("editor.ai.exampleExplanationLabel")}
        </label>
        <textarea
          rows={4}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          onBlur={() => void handleSave()}
          placeholder={t("editor.ai.exampleExplanationPlaceholder")}
          className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary resize-y"
        />
      </div>
      {saveError && (
        <p className="mt-1.5 text-xs text-error-dark">{saveError}</p>
      )}
    </div>
  );
}
